import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { t } from 'i18next'
import { StyledContainerColumnLeft, StyledContainerRowBetween } from '../../../styles/ContainerStyles'
import { Add, Check, Close, ContentCopy, Payments } from '@mui/icons-material'
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/system'
import CostTable from '../../Table/Cost/CostTable'
import { RequestProps, User } from '../../../store/types'
import defaultProps from '../../../config/defaultProps'
import { useCreateCostMutation, useEditCostMutation, useGetCostsQuery } from '../../../store/redux/reducers/costSlice'
import Cost from '../../../store/types/models/Cost'
import dayjs from 'dayjs'
import CostViewForm from './CostViewForm'
import { Control, FormState, UseFormGetValues, UseFormHandleSubmit, UseFormReset, UseFormWatch } from 'react-hook-form'
import { CostFormValues, defaultCostFormValues } from '../../../store/types/form/CostFormValues'
import { useNotification } from '../../../store/redux/hooks'
import { createErrorMessage } from '../../../store/utils'
import { SquareButton } from '../../../styles/ButtonStyles'
import { isEqual } from 'lodash'
import { useSelector } from 'react-redux'
import { getCurrentUser, getIsLeaderAndNotManagement } from '../../../store/redux/reducers/currentUserSlice'
import DateFormat from '../../../store/types/enums/DateFormat'
import { LoadingButton } from '@mui/lab'
import LoadingWrapper from '../../Loading/LoadingWrapper'
import _ from 'lodash'
import ConfirmationDialog from '../../Dialog/ConfirmationDialog'
import { usePopupState } from 'material-ui-popup-state/hooks'
import CostDuplicatePopup from './CostDuplicatePopup'

type CostContentProps = {
  date: string
  employeeId: string | undefined
  selectedEmployee: User | null
  setIsChangePresent: Dispatch<SetStateAction<boolean>>
  setIsCancelFormButtonClick: Dispatch<SetStateAction<boolean>>
  handleSubmit: UseFormHandleSubmit<CostFormValues>
  control: Control<CostFormValues>
  reset: UseFormReset<CostFormValues>
  getValues: UseFormGetValues<CostFormValues>
  formState: FormState<CostFormValues>
  costToEdit: Cost | null
  setCostToEdit: Dispatch<SetStateAction<Cost | null>>
  isViewForm: boolean
  setIsViewForm: Dispatch<SetStateAction<boolean>>
  handleResetForm: () => void
  watch: UseFormWatch<CostFormValues>
  selectedDate: string
}

const CostContent = ({
  date,
  employeeId,
  selectedEmployee,
  setIsChangePresent,
  setIsCancelFormButtonClick,
  handleSubmit,
  control,
  reset,
  getValues,
  formState,
  costToEdit,
  setCostToEdit,
  isViewForm,
  setIsViewForm,
  handleResetForm,
  watch,
  selectedDate,
}: CostContentProps) => {
  const { displayNotification } = useNotification()
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  let formattedDate = dayjs(date).format(`${DateFormat.MonthName} • ${DateFormat.Year}`)
  const currentUser = useSelector(getCurrentUser)
  const isLeaderAndNotManagement = useSelector(getIsLeaderAndNotManagement)
  const isMyCosts = currentUser?.id === employeeId
  const [requestProps, setRequestProps] = useState<RequestProps>(defaultProps)
  const { data: costsResponse, isLoading } = useGetCostsQuery({
    ...requestProps,
    yearMonth: dayjs(date).format(DateFormat.YearMonth),
    'author.id': employeeId,
  })

  const [editCost] = useEditCostMutation()

  const handleSave = () => {
    if (!isViewForm) {
      setIsViewForm(true)
    } else {
      if (costToEdit === null) {
        if (watch('monthRepeat') && isValid) setOpenConfirmationDialog(true)
        else handleSubmitCreate()
      } else handleSubmitEdit()
    }
  }

  const { isSubmitting, isDirty, isValid } = formState

  useEffect(() => {
    setIsChangePresent(isDirty)
  }, [isDirty, setIsChangePresent])

  const [createCost] = useCreateCostMutation()

  const handleCreate = async (data: CostFormValues) => {
    try {
      await createCost(_.mapValues(data)).unwrap()
      displayNotification({ message: t('components.costSidebar.alert.costCreated'), type: 'success' })
      reset()
      setIsViewForm(false)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('page.users.message.error')} ${errorDescription}`, type: 'error' })
    }
  }

  const handleSubmitCreate = () => {
    handleSubmit(data => handleCreate(data))()
  }

  const handleCancel = (triggeredBy?: 'button' | 'function') => {
    if (triggeredBy === 'button' && formState.isDirty) {
      setIsCancelFormButtonClick(true)
    } else {
      handleResetForm()
    }
  }

  const updateForm = useCallback(
    (costData: Cost | null) => {
      if (costData === null) {
        return
      }

      const updatedData: CostFormValues = {
        ...costData,
        startDate: dayjs(costData.startDate),
        endDate: dayjs(costData.endDate),
        quantity: costData.quantity.toString(),
        projectId: costData.project?.id || null,
      }

      reset(updatedData)
    },
    [reset]
  )

  useEffect(() => {
    if (costToEdit) {
      updateForm(costToEdit)
      setIsViewForm(true)
    } else {
      updateForm(null)
      reset()
    }
  }, [updateForm, costToEdit, reset, setIsViewForm])

  const handleEdit = async (data: CostFormValues) => {
    if (costToEdit !== null) {
      const editCostToFormValues: CostFormValues = {
        ...costToEdit,
        startDate: dayjs(costToEdit.startDate),
        endDate: dayjs(costToEdit.endDate),
        quantity: costToEdit.quantity.toString(),
        projectId: costToEdit.project?.id || null,
      }
      if (isEqual(data, editCostToFormValues)) {
        handleCancel()
        return
      }

      try {
        await editCost({ id: costToEdit.id, cost: _.mapValues(data) }).unwrap()
        displayNotification({ message: t('components.costSidebar.alert.costEdited'), type: 'success' })
        setCostToEdit(null)
        setIsViewForm(false)
        reset({
          ...defaultCostFormValues,
          startDate: dayjs(selectedDate).startOf('month').utc(true),
          endDate: dayjs(selectedDate).endOf('month').utc(true),
        })
      } catch (error) {
        let errorDescription = createErrorMessage(error)
        displayNotification({
          message: `${t('page.users.message.error')} ${errorDescription}`,
          type: 'error',
        })
        reset(data)
      }
    }
  }
  const handleSubmitEdit = () => {
    handleSubmit(data => handleEdit(data))()
  }

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false)
  const [duplicateMode, setDuplicateMode] = useState<boolean>(false)
  const [selected, setSelected] = useState<Cost[]>([])
  const targetRef = useRef(null)
  const popupStatePopover = usePopupState({ variant: 'popover', popupId: 'duplicate-cost-for-month' })

  const handleCancelDuplicateMode = () => {
    setDuplicateMode(false)
    setSelected([])
  }

  const handleDuplicateButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!duplicateMode) {
      setDuplicateMode(true)
    } else {
      popupStatePopover.open(event)
    }
  }

  useEffect(() => {
    if (isViewForm) handleCancelDuplicateMode()
  }, [isViewForm])

  return (
    <Stack sx={{ pt: 6 }} gap={4}>
      <StyledContainerRowBetween sx={{ flexDirection: isDownSm ? 'column' : 'row' }}>
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Payments sx={{ fontSize: 36 }} />
          <StyledContainerColumnLeft>
            <Typography variant="h5" component="h1" color={'text.highEmphasis'}>
              {!isViewForm
                ? t('components.costSidebar.title')
                : costToEdit === null
                ? t('components.costSidebar.newCost')
                : t('components.costSidebar.editCost')}
            </Typography>
            <Typography
              color={'text.highEmphasis'}
            >{`${selectedEmployee?.firstName} ${selectedEmployee?.lastName}`}</Typography>
            <Typography color={'text.highEmphasis'}>{`${formattedDate}`}</Typography>
          </StyledContainerColumnLeft>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: isDownSm ? '100%' : 'auto' }}>
          {isViewForm && (
            <SquareButton
              onClick={() => handleCancel('button')}
              color="secondary"
              disabled={isSubmitting}
              aria-label={t('components.costSidebar.buttonCancel')}
            >
              <Close />
            </SquareButton>
          )}
          <LoadingButton
            startIcon={!isViewForm ? <Add /> : <Check />}
            variant="contained"
            size="medium"
            onClick={handleSave}
            sx={{ display: isMyCosts || isViewForm ? 'flex' : 'none' }}
            disabled={isSubmitting}
            loading={isSubmitting}
            fullWidth={isDownSm}
            aria-label={
              !isViewForm ? t('components.costSidebar.buttonAddCost') : t('components.costSidebar.buttonSaveCost')
            }
          >
            {!isViewForm ? t(`components.costSidebar.buttonAddCost`) : t(`components.costSidebar.buttonSaveCost`)}
          </LoadingButton>
        </Box>
      </StyledContainerRowBetween>
      {!isViewForm && (costsResponse?.content?.length ?? 0) > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'end', gap: 1 }}>
          {duplicateMode && (
            <SquareButton
              onClick={handleCancelDuplicateMode}
              color="secondary"
              aria-label={t('components.costSidebar.buttonCancel')}
            >
              <Close />
            </SquareButton>
          )}
          <Button
            ref={targetRef}
            variant="contained"
            color="secondary"
            className={duplicateMode ? 'selected' : ''}
            startIcon={!duplicateMode ? <ContentCopy /> : <Check />}
            disabled={duplicateMode && selected.length <= 0}
            onClick={handleDuplicateButtonClick}
          >
            {t('components.costSidebar.buttonDuplicate')}
          </Button>
        </Box>
      )}
      <Stack>
        {!isViewForm ? (
          <LoadingWrapper isLoading={isLoading}>
            <CostTable
              setRequestProps={setRequestProps}
              costs={costsResponse?.content || []}
              totalElements={costsResponse?.totalElements || 0}
              setCostToEdit={setCostToEdit}
              isLeader={isLeaderAndNotManagement}
              isMyCosts={isMyCosts}
              duplicateMode={duplicateMode}
              selected={selected}
              setSelected={setSelected}
            />
          </LoadingWrapper>
        ) : (
          <CostViewForm control={control} getValues={getValues} watch={watch} isEdit={costToEdit !== null} />
        )}
      </Stack>
      <ConfirmationDialog
        open={openConfirmationDialog}
        setOpen={setOpenConfirmationDialog}
        handleConfirm={() => {
          handleSubmitCreate()
          setOpenConfirmationDialog(false)
        }}
        title={t('components.costSidebar.dialog.confirmation.title', {
          count: Number(watch('monthRepeatAmount')) || 0,
        })}
        content={t('components.costSidebar.dialog.confirmation.content')}
        handleClose={() => setOpenConfirmationDialog(false)}
      />
      <CostDuplicatePopup
        targetRef={targetRef}
        popupState={popupStatePopover}
        month={dayjs(selectedDate).startOf('month').utc(true)}
        selectedCosts={selected}
        setDuplicateMode={setDuplicateMode}
      />
    </Stack>
  )
}

export default CostContent
